import { Link } from "react-router";
import type { Product } from "../../../shared/types.ts";

type Props = {
  product: Product;
};

export default function CandleCard({ product }: Props) {
  const { slug, name, subtitle, scent, description, priceLabel, tags, image } =
    product;
  return (
    <Link
      to={`/product/${slug}`}
      className="group relative flex flex-col overflow-hidden border border-[var(--color-rule)]/60 bg-[var(--color-warm-white)] transition-all duration-500 hover:shadow-[0_34px_70px_-40px_rgba(15,71,49,0.5)]"
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-[var(--color-warm-white)]">
        <img
          src={image}
          alt={name}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.2,0.7,0.2,1)] group-hover:scale-[1.04]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[var(--color-dark)]/15 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        />
      </div>

      <div className="flex flex-1 flex-col px-7 pt-6 pb-7">
        <div className="flex flex-wrap gap-1.5">
          {tags.map((tag) => (
            <span
              key={tag}
              className={[
                "rounded-full border px-2.5 py-0.5 text-[0.6rem] font-medium uppercase tracking-[0.14em]",
                tag === "Bestseller"
                  ? "border-[var(--color-amber)] bg-[var(--color-amber)] text-[var(--color-warm-white)]"
                  : tag === "New"
                    ? "border-[var(--color-emerald)] bg-[var(--color-emerald-deep)] text-[var(--color-sage-light)]"
                    : tag === "Signature"
                      ? "border-[var(--color-gold)] bg-transparent text-[var(--color-gold-deep)]"
                      : tag === "Made to Order"
                        ? "border-[var(--color-oxblood)]/60 bg-transparent text-[var(--color-oxblood)]"
                        : "border-[var(--color-sage-light)] bg-transparent text-[var(--color-sage)]",
              ].join(" ")}
            >
              {tag}
            </span>
          ))}
        </div>

        <h3 className="mt-4 font-serif text-2xl leading-tight text-[var(--color-emerald-deep)] transition-colors duration-300 group-hover:text-[var(--color-emerald)]">
          {name}
        </h3>
        {subtitle ? (
          <p className="mt-1 font-serif text-xs italic tracking-wide text-[var(--color-amber)]">
            {subtitle}
          </p>
        ) : null}
        <p className="mt-3 font-serif text-sm italic text-[var(--color-stone)]">
          {scent}
        </p>
        <p className="mt-4 line-clamp-3 text-[0.82rem] leading-[1.65] text-[var(--color-charcoal)]/75">
          {description}
        </p>

        <div className="mt-auto flex items-end justify-between pt-7">
          <span className="font-serif text-xl text-[var(--color-emerald-deep)]">
            {priceLabel}
          </span>
          <span className="flex items-center gap-2 text-[0.62rem] font-medium uppercase tracking-[0.18em] text-[var(--color-stone)] transition-all duration-300 group-hover:gap-3 group-hover:text-[var(--color-amber)]">
            View <span aria-hidden>→</span>
          </span>
        </div>
      </div>
    </Link>
  );
}
